import { ChevronLeft, ChevronRight, Maximize2, Minimize2 } from 'lucide-react';
import type { KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DeliverySelection } from '~/components/common/delivery-run-panel';
import type {
  EnrichmentAttributeGroup,
  EnrichmentRunPanelAction,
  EnrichmentRunPanelState,
} from '~/components/common/enrichment-run-panel';
import { SearchInput } from '~/components/common/search-input';
import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { useCanTranslateContent } from '~/hooks/useCanTranslateContent';
import { cn } from '~/utils/cn';

import { useBatchHeader, useProductSearchDescriptors } from '../../hooks/use-batch-detail';
import {
  buildSelectedOptimizations,
  useDeliveryCompareRail,
  usePreEnrichmentSnapshot,
  useSelectedEnrichment,
} from '../../hooks/use-delivery-compare';
import type { LanguageApprovalOption } from '../../hooks/use-language-approval';
import type { Product } from '../../types/catalog-operations.types';
import { computeProposedFieldStatus } from '../../utils/compute-proposed-delivery-status';
import { resolveDescriptionDeliveryView } from '../../utils/description-delivery-view';
import {
  composeSearchDescriptorDescription,
  joinSearchPhrases,
} from '../../utils/search-descriptor-description';
import { LanguageCompareView } from '../translations/language-compare-view';

import { DeliveryWizardAttributePopover } from './delivery-wizard-attribute-popover';
import {
  DeliveryWizardComparisonHeader,
  DeliveryWizardComparisonRow,
} from './delivery-wizard-comparison-table';
import { DeliveryWizardLanguageSummary } from './delivery-wizard-language-summary';
import { DeliveryWizardPreviewPager } from './delivery-wizard-preview-pager';
import { DeliveryWizardPreviewRail } from './delivery-wizard-preview-rail';
import { DeliveryWizardPreviewStrip } from './delivery-wizard-preview-strip';

type DetailTab = 'attributes' | 'languages';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 500;

interface Props {
  batchId: string;
  curationRefId: string | undefined;
  platform: 'google' | 'meta';
  /** Short-form connector code (`GMC`/`META`) the enrichment read wants —
   *  derived by the caller the same way `delivery-compare-page.tsx` does. */
  enrichmentConnectorCode: string | undefined;
  /** The operator's Step 1 selection — drives which attributes render and
   *  the header's "M attributes selected" badge. */
  selection: DeliverySelection;
  /** Step 1's attribute taxonomy + reducer state/dispatch, passed through so
   *  the attributes badge can open a quick-edit popover without navigating
   *  back to Decision — same `dispatch` Decision uses, so every consumer of
   *  `selection` here stays in sync for free. */
  groups: EnrichmentAttributeGroup[];
  attrState: EnrichmentRunPanelState;
  dispatch: (action: EnrichmentRunPanelAction) => void;
  /** Step 1's approved-locale state (Google only) — read-only here, mirrored
   *  from `useLanguageApproval` at the page level. Editing still only happens
   *  on Decision's `LanguageApprovalSection`. */
  languageOptions: LanguageApprovalOption[];
  selectedLanguages: string[];
  /** Batch-level fallback for the per-product translations read, same
   *  precedence `product-detail-page.tsx` uses: prefer the product's own
   *  enrichment run, fall back to the batch's. */
  batchWorkflowId: string | undefined;
  /** Full-screen toggle — owned by `DeliveryWizardPage` (it hides its own
   *  header/stepper and drops its own width cap), mirrored here only to
   *  render the toggle button and handle Escape. */
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}

/**
 * Step 2 of the delivery wizard — pre-delivery preview. Reuses the same
 * master-detail data hooks the post-delivery Compare Delivery page uses for
 * its pre-enrichment/enriched panes (`usePreEnrichmentSnapshot`,
 * `useSelectedEnrichment`, `buildSelectedOptimizations`), but shows only the
 * attributes selected in Step 1 (spec §3.4) instead of every enriched field —
 * every row rendered here is therefore, by definition, going to be delivered,
 * and computes its own "Enriched"/"Proposed" values client-side from the
 * selection instead of fetching a real delivered-payload lookup, since
 * nothing has been committed yet.
 *
 * Its own Back/Confirm actions live in the shared `DeliveryWizardFooter`
 * (rendered by `DeliveryWizardPage`), not here.
 *
 * The "description" row is special-cased: Description, Search Descriptors,
 * and Phrase Descriptions are alternate forms of the same field (PR #750,
 * `docs/product-description-view-toggle.md`), so at most one ships even if
 * the operator selected more than one — `resolveDescriptionDeliveryView`
 * picks the winner (Phrase Descriptions > Search Descriptors > Description).
 */
export function DeliveryWizardPreviewStep({
  batchId,
  curationRefId,
  platform,
  enrichmentConnectorCode,
  selection,
  groups,
  attrState,
  dispatch,
  languageOptions,
  selectedLanguages,
  batchWorkflowId,
  isExpanded,
  onExpandedChange,
}: Props) {
  const { t } = useTranslation('catalog-operations');
  const { t: tCommon } = useTranslation('common');
  const canTranslate = useCanTranslateContent();
  // Same gate `product-detail-page.tsx` uses before mounting `LanguageCompareView`
  // (internal-user permission + Google only) — `LanguageCompareView`'s own query
  // has no permission gate of its own, so this must be checked before rendering it,
  // not just inferred from `languageOptions.length` (which already reflects the
  // same permission via `useLanguageApproval`, but is a separate object).
  const languagesAvailable = canTranslate && platform === 'google' && languageOptions.length > 0;
  const [detailTab, setDetailTab] = useState<DetailTab>('attributes');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Collapses the product list to a thin strip so the comparison table gets
  // the width; the strip's prev/next buttons still step products meanwhile.
  const [isRailCollapsed, setIsRailCollapsed] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);
  // Set when stepping backward across a page boundary, so the auto-select
  // below lands on the new page's last row instead of its first.
  const selectLastOnLoadRef = useRef(false);

  // 500ms debounce per project convention — the rail is server-paged/searched.
  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
  };
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  // Escape collapses full-screen (never expands) — only listens while
  // expanded, so it never intercepts Escape elsewhere in the wizard.
  useEffect(() => {
    if (!isExpanded) return;
    function handleKeyDown(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') onExpandedChange(false);
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isExpanded, onExpandedChange]);

  const rail = useDeliveryCompareRail(curationRefId, batchId, page, PAGE_SIZE, debouncedSearch);
  const products = rail.data?.products ?? [];
  const totalRows = rail.data?.total ?? 0;

  // Auto-select the first row of a page (or the last, after stepping back
  // across a boundary) whenever its product set changes and nothing on it is
  // currently selected.
  useEffect(() => {
    if (products.length === 0) return;
    if (!products.some((p) => p.id === selectedId)) {
      const edge = selectLastOnLoadRef.current ? products.at(-1) : products[0];
      selectLastOnLoadRef.current = false;
      setSelectedId(edge?.id ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when the page's product set changes
  }, [products]);

  // Step one product forward/back within the loaded page, crossing into the
  // adjacent page at a boundary. A server-paged rail can't select a row on a
  // page it hasn't fetched yet, so a boundary step just changes page and the
  // auto-select effect above picks the first (forward) or last (backward) row
  // once that page's data lands. Shared by the rail's arrow keys and the
  // strip's prev/next buttons.
  const currentIndex = products.findIndex((p) => p.id === selectedId);
  const hasPreviousProduct = currentIndex > 0 || page > 1;
  const hasNextProduct =
    (currentIndex !== -1 && currentIndex < products.length - 1) || page * PAGE_SIZE < totalRows;
  function stepProduct(delta: 1 | -1) {
    if (currentIndex === -1) return;
    const next = products[currentIndex + delta];
    if (next) {
      setSelectedId(next.id);
      return;
    }
    if (delta > 0 && page * PAGE_SIZE < totalRows) {
      setPage(page + 1);
    } else if (delta < 0 && page > 1) {
      selectLastOnLoadRef.current = true;
      setPage(page - 1);
    }
  }

  function handleListKeyDown(e: KeyboardEvent<HTMLUListElement>) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    stepProduct(e.key === 'ArrowDown' ? 1 : -1);
  }

  const selectedProduct: Product | undefined = products.find((p) => p.id === selectedId);

  const preEnrichment = usePreEnrichmentSnapshot(selectedProduct, batchId);
  const enrichment = useSelectedEnrichment(selectedProduct, enrichmentConnectorCode);
  const optimizations = buildSelectedOptimizations(enrichment.data?.optimizations, preEnrichment);

  // Description / Search Descriptors / Phrase Descriptions are alternate forms
  // of the same field (see `description-delivery-view.ts`) — resolve which one
  // actually ships, then fetch what that view needs to render (same
  // `enrichmentJobId`-pinned read `product-detail-page.tsx` uses, so the read
  // stays scoped to this batch's producer rather than the product's most
  // recent run elsewhere).
  const descriptionView = resolveDescriptionDeliveryView(selection);
  // Skip the read when the run produced neither strategy (header echo; only an
  // explicit `false` counts). The wizard page already loaded the header, so this
  // is a cache hit and the read isn't held back.
  const { data: batchHeader } = useBatchHeader(batchId);
  const descriptorsNeverRan =
    batchHeader?.searchDescriptorsEnabled === false &&
    batchHeader.phraseDescriptionsEnabled === false;
  const { data: searchDescriptors } = useProductSearchDescriptors(
    selectedProduct?.id ?? '',
    !!selectedProduct && !descriptorsNeverRan,
    batchId,
  );
  const searchPhrasesJoined = joinSearchPhrases(searchDescriptors);
  // Only Level 3 (the rewrite of the original copy) is what Phrase Descriptions
  // delivers — Levels 1/2 are intermediate derivations, so Preview neither
  // shows them nor falls back to them when Level 3 is missing.
  const phraseLevel3 = searchDescriptors?.phraseDescription?.level3Description?.trim() ?? '';
  const phraseLevel3Error = searchDescriptors?.phraseDescription?.level3Error ?? undefined;

  // Selecting Search Descriptors or Phrase Descriptions alone (without the
  // plain "Description" attribute also checked) still delivers the
  // description slot, just via a different form — computeProposedFieldStatus
  // only knows the field's own key, so the description-family precedence
  // overrides it here.
  function proposedStatusFor(field: string) {
    if (field === 'description' && descriptionView) return 'sent';
    return computeProposedFieldStatus(field, selection);
  }

  const proposedCount = optimizations.filter(
    (opt) => proposedStatusFor(opt.field) === 'sent',
  ).length;

  // Only the attributes selected in Step 1 render here (spec §3.4) — every
  // row is therefore unconditionally "will be delivered", not a mix of
  // included/excluded. `description` is pulled out and handled once, via
  // whichever view won, rather than joining the plain filter below.
  const rows = optimizations.flatMap((opt) => {
    if (opt.field === 'description') {
      if (!descriptionView) return [];
      if (descriptionView === 'phrase-descriptions') {
        // Same pre | enriched | proposed row as every other attribute, with the
        // enriched value swapped for Level 3 so diff + proposed track it.
        return [
          <DeliveryWizardComparisonRow
            key={opt.field}
            opt={{ ...opt, value: phraseLevel3 }}
            {...(!phraseLevel3 && phraseLevel3Error ? { enrichedError: phraseLevel3Error } : {})}
          />,
        ];
      }
      // Search Descriptors ships `<default description> | <phrases>` — the
      // phrases ride on the pre-enrichment copy, not the enriched one. No
      // phrases → the pre-enrichment copy ships alone, matching the export's
      // `compose_description` (base = original for every row, tags or not);
      // the enriched copy is only the fallback when there is no original.
      const overrideValue =
        descriptionView === 'search-descriptors'
          ? composeSearchDescriptorDescription(
              opt.currentValue?.trim() ? opt.currentValue : opt.value,
              searchPhrasesJoined,
            )
          : undefined;
      return [
        <DeliveryWizardComparisonRow
          key={opt.field}
          opt={opt}
          {...(overrideValue !== undefined ? { overrideValue } : {})}
          {...(overrideValue !== undefined && searchPhrasesJoined
            ? { highlightedSuffix: searchPhrasesJoined }
            : {})}
        />,
      ];
    }
    if (proposedStatusFor(opt.field) !== 'sent') return [];
    return [<DeliveryWizardComparisonRow key={opt.field} opt={opt} />];
  });

  return (
    <div
      className="flex h-full min-h-0 flex-1 flex-col"
      data-testid="delivery-wizard-step-panel-preview"
    >
      <div className="shrink-0 pb-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2
              className="text-xl font-semibold tracking-tight"
              data-testid="delivery-wizard-preview-title"
            >
              {tCommon('deliveryWizard.preview.title')}
            </h2>
            <p className="text-muted-foreground mt-1 max-w-[62ch] text-sm">
              {tCommon('deliveryWizard.preview.subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span
              className="bg-background rounded-md border px-2.5 py-1"
              data-testid="delivery-wizard-preview-products-badge"
            >
              <span className="font-semibold" data-testid="delivery-wizard-preview-products-value">
                {totalRows}
              </span>{' '}
              {tCommon('deliveryWizard.preview.badges.products')}
            </span>
            <DeliveryWizardAttributePopover
              groups={groups}
              state={attrState}
              dispatch={dispatch}
              selectedCount={selection.attributeKeys.length}
            />
            <Tooltip delayDuration={150}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-auto w-auto cursor-pointer rounded-md border p-1.5"
                  data-testid="delivery-wizard-preview-expand-toggle"
                  aria-label={
                    isExpanded
                      ? tCommon('deliveryWizard.preview.expand.collapseLabel')
                      : tCommon('deliveryWizard.preview.expand.expandLabel')
                  }
                  onClick={() => {
                    onExpandedChange(!isExpanded);
                  }}
                >
                  {isExpanded ? (
                    <Minimize2 aria-hidden="true" className="size-3.5" />
                  ) : (
                    <Maximize2 aria-hidden="true" className="size-3.5" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                {isExpanded
                  ? tCommon('deliveryWizard.preview.expand.collapseLabel')
                  : tCommon('deliveryWizard.preview.expand.expandLabel')}
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
        {languagesAvailable && (
          <div className="mt-3">
            <DeliveryWizardLanguageSummary options={languageOptions} selected={selectedLanguages} />
          </div>
        )}
      </div>

      <div
        className={cn(
          'grid min-h-0 flex-1 grid-cols-1 gap-4',
          isRailCollapsed
            ? 'lg:grid-cols-[44px_minmax(0,1fr)]'
            : 'lg:grid-cols-[340px_minmax(0,1fr)]',
        )}
      >
        {isRailCollapsed ? (
          <div
            className="bg-background flex flex-col items-center rounded-2xl border py-3"
            data-testid="delivery-wizard-preview-rail-collapsed"
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 cursor-pointer"
                  aria-label={tCommon('deliveryWizard.preview.rail.expand')}
                  data-testid="delivery-wizard-preview-rail-expand-button"
                  onClick={() => {
                    setIsRailCollapsed(false);
                  }}
                >
                  <ChevronRight aria-hidden="true" className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                {tCommon('deliveryWizard.preview.rail.expand')}
              </TooltipContent>
            </Tooltip>
          </div>
        ) : (
          <div
            className="bg-background flex min-h-0 flex-col rounded-2xl border"
            data-testid="delivery-wizard-preview-rail-panel"
          >
            <div className="flex shrink-0 items-center gap-1.5 border-b p-3">
              <div className="min-w-0 flex-1" data-testid="delivery-wizard-preview-search">
                <SearchInput
                  value={search}
                  onChange={handleSearchChange}
                  placeholder={tCommon('deliveryWizard.preview.rail.searchPlaceholder')}
                  testId="delivery-wizard-preview-search-field"
                />
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0 cursor-pointer"
                    aria-label={tCommon('deliveryWizard.preview.rail.collapse')}
                    data-testid="delivery-wizard-preview-rail-collapse-button"
                    onClick={() => {
                      setIsRailCollapsed(true);
                    }}
                  >
                    <ChevronLeft aria-hidden="true" className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {tCommon('deliveryWizard.preview.rail.collapse')}
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="scrollbar-transparent min-h-0 flex-1 overflow-y-auto">
              <DeliveryWizardPreviewRail
                testId="delivery-wizard-preview-rail"
                products={products}
                selectedId={selectedId}
                onSelect={(p) => {
                  setSelectedId(p.id);
                }}
                onKeyDown={handleListKeyDown}
                isLoading={rail.isLoading}
              />
            </div>
            <div className="shrink-0 border-t p-2.5">
              <DeliveryWizardPreviewPager
                page={page}
                pageSize={PAGE_SIZE}
                totalRows={totalRows}
                onPageChange={setPage}
              />
            </div>
          </div>
        )}

        <div className="flex min-h-0 min-w-0 flex-col space-y-4">
          {selectedProduct ? (
            <>
              <div className="shrink-0">
                <DeliveryWizardPreviewStrip
                  product={selectedProduct}
                  proposedCount={proposedCount}
                  totalCount={optimizations.length}
                  onPrevious={() => {
                    stepProduct(-1);
                  }}
                  onNext={() => {
                    stepProduct(1);
                  }}
                  hasPrevious={hasPreviousProduct}
                  hasNext={hasNextProduct}
                />
              </div>
              {languagesAvailable && (
                <div className="shrink-0">
                  <SegmentedTabs
                    value={detailTab}
                    onChange={setDetailTab}
                    ariaLabel="Preview detail sections"
                    testId="delivery-wizard-preview-tabs"
                    className="bg-selected-row border-0 sm:max-w-xs"
                    options={[
                      {
                        value: 'attributes',
                        label: tCommon('deliveryWizard.preview.languages.tabAttributes'),
                      },
                      {
                        value: 'languages',
                        label: tCommon('deliveryWizard.preview.languages.tabLanguages'),
                      },
                    ]}
                  />
                </div>
              )}
              {languagesAvailable && detailTab === 'languages' ? (
                <div
                  className="min-h-0 flex-1 overflow-y-auto"
                  data-testid="delivery-wizard-preview-languages-panel"
                >
                  <LanguageCompareView
                    workflowId={enrichment.data?.workflowId ?? batchWorkflowId ?? ''}
                    productId={selectedProduct.id}
                  />
                </div>
              ) : (
                <div
                  className="bg-background flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border"
                  data-testid="delivery-wizard-preview-comparison-table"
                >
                  {enrichment.isLoading || preEnrichment.isLoading ? (
                    <div
                      className="space-y-4 p-4"
                      data-testid="delivery-wizard-preview-comparison-loading"
                    >
                      {Array.from({ length: 4 }, (_, i) => (
                        <Skeleton key={i} className="h-24 w-full" />
                      ))}
                    </div>
                  ) : rows.length === 0 ? (
                    <p
                      className="text-muted-foreground p-6 text-center text-sm"
                      data-testid="delivery-wizard-preview-comparison-empty"
                    >
                      {t('deliveryCompare.attributeTable.empty')}
                    </p>
                  ) : (
                    <>
                      <DeliveryWizardComparisonHeader />
                      <div className="scrollbar-transparent min-h-0 flex-1 overflow-y-auto">
                        {rows}
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          ) : (
            <div
              className="bg-background text-muted-foreground flex h-64 items-center justify-center rounded-2xl border text-sm"
              data-testid="delivery-wizard-preview-no-selection"
            >
              {t('deliveryCompare.noSelection')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
